import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, TextField, Button, Paper, Alert, CircularProgress,
  Fade, Chip, Divider, InputAdornment, IconButton, Tooltip, Grid,
} from '@mui/material';
import {
  Cloud, Search, Rocket, Copy, Check, ExternalLink, Trash2,
  AlertTriangle, CheckCircle2, Globe, Sparkles,
} from 'lucide-react';
import { styled } from '@mui/material/styles';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL;

const SectionCard = styled(Paper)(() => ({
  background: '#141926',
  border: '1px solid rgba(255,255,255,0.06)',
  borderRadius: 16,
  padding: 24,
  marginBottom: 24,
}));

const formatDomain = (input) =>
  input
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-').replace(/^-+/g, '').substring(0, 50);

const formatCNPJ = (v) => {
  const n = v.replace(/\D/g, '').substring(0, 14);
  if (n.length <= 2) return n;
  if (n.length <= 5) return `${n.slice(0, 2)}.${n.slice(2)}`;
  if (n.length <= 8) return `${n.slice(0, 2)}.${n.slice(2, 5)}.${n.slice(5)}`;
  if (n.length <= 12) return `${n.slice(0, 2)}.${n.slice(2, 5)}.${n.slice(5, 8)}/${n.slice(8)}`;
  return `${n.slice(0, 2)}.${n.slice(2, 5)}.${n.slice(5, 8)}/${n.slice(8, 12)}-${n.slice(12)}`;
};

const getUserData = () => {
  try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; }
};

export default function CloudflareSites() {
  const navigate = useNavigate();
  const isAdmin = getUserData().tipo === 'admin';

  const [cfConfig, setCfConfig] = useState({ loading: true, configured: false, subdomain: null });
  const [sites, setSites] = useState([]);
  const [loadingSites, setLoadingSites] = useState(true);

  const [cnpjInput, setCnpjInput] = useState('');
  const [cnpjLoading, setCnpjLoading] = useState(false);
  const [cnpjData, setCnpjData] = useState(null);
  const [iaLoading, setIaLoading] = useState(false);

  const [form, setForm] = useState({
    DOMINIO: '', 'RAZÃO': '', CNPJ: '', TELEFONE: '', EMAIL: '',
    WHATSAPP: '', INSTAGRAM: '', LINK_PAGINA: '', 'MISSÃO': '',
    SOBRE: '', RODAPE: '', META_TAG: '',
  });

  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [lastUrl, setLastUrl] = useState('');
  const [copied, setCopied] = useState('');

  const authHeader = useCallback(() => {
    const token = localStorage.getItem('token');
    return { headers: { Authorization: `Bearer ${token}` } };
  }, []);

  const setField = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  const fetchConfig = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/cloudflare/config`, authHeader());
      setCfConfig({ loading: false, configured: !!res.data.configured, subdomain: res.data.subdomain });
    } catch {
      setCfConfig({ loading: false, configured: false, subdomain: null });
    }
  }, [authHeader]);

  const fetchSites = useCallback(async () => {
    setLoadingSites(true);
    try {
      const res = await axios.get(`${API}/api/cloudflare/sites`, authHeader());
      if (res.data.success) setSites(res.data.sites || []);
    } catch { /* ignore */ }
    setLoadingSites(false);
  }, [authHeader]);

  useEffect(() => {
    if (!isAdmin) { navigate('/dashboard', { replace: true }); return; }
    fetchConfig();
    fetchSites();
  }, [isAdmin, navigate, fetchConfig, fetchSites]);

  const handleBuscarCNPJ = async () => {
    const cnpjLimpo = cnpjInput.replace(/\D/g, '');
    if (cnpjLimpo.length !== 14) { setError('CNPJ deve ter 14 dígitos'); return; }
    setError(''); setCnpjLoading(true);
    try {
      const res = await axios.get(`${API}/api/sites/cnpj/${cnpjLimpo}`, { ...authHeader(), timeout: 15000 });
      if (res.data.success) {
        const d = res.data.data;
        setCnpjData(d);
        setForm(prev => ({
          ...prev,
          DOMINIO: prev.DOMINIO || formatDomain(d.razao_social || d.nome_fantasia || 'meusite'),
          'RAZÃO': d.razao_social || prev['RAZÃO'],
          CNPJ: formatCNPJ(String(d.cnpj || cnpjLimpo)),
          TELEFONE: d.ddd_telefone1 || d.telefone1 || prev.TELEFONE,
          WHATSAPP: prev.WHATSAPP || d.ddd_telefone1 || d.telefone1 || '',
          EMAIL: d.email || prev.EMAIL,
        }));
        setSuccess('Dados carregados. Clique em "Gerar textos com IA" ou preencha manualmente.');
      } else { setError(res.data.message || 'CNPJ não encontrado'); }
    } catch (err) { setError(err.response?.data?.message || 'Erro ao buscar CNPJ'); }
    finally { setCnpjLoading(false); }
  };

  const handleGerarTextos = async () => {
    if (!cnpjData) { setError('Busque um CNPJ primeiro para a IA gerar os textos.'); return; }
    setError(''); setSuccess(''); setIaLoading(true);
    try {
      const d = cnpjData;
      const endereco = [d.logradouro, d.numero, d.bairro, `${d.municipio}-${d.uf}`, `CEP ${d.cep}`].filter(Boolean).join(', ');
      const cnaePrincipal = d.cnae_fiscal_descricao ? { codigo: String(d.cnae_fiscal), descricao: d.cnae_fiscal_descricao } : null;
      const cnaesSecundarios = (d.cnaes_secundarios || []).map(c => ({ codigo: String(c.codigo), descricao: c.descricao }));
      const situacaoMap = { 1: 'Nula', 2: 'Ativa', 3: 'Suspensa', 4: 'Inapta', 8: 'Baixada' };
      const res = await axios.post(
        `${API}/api/sites/gerar-textos-ia`,
        {
          razao_social: d.razao_social,
          nome_fantasia: d.nome_fantasia || d.razao_social,
          cnpj: formatCNPJ(String(d.cnpj)),
          data_abertura: d.data_inicio_atividade ? new Date(d.data_inicio_atividade).toLocaleDateString('pt-BR') : 'N/A',
          porte: d.porte || d.descricao_porte || 'N/A',
          natureza_juridica: d.natureza_juridica || d.descricao_natureza_juridica || 'N/A',
          capital_social: (d.capital_social || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 }),
          situacao_cadastral: situacaoMap[d.situacao_cadastral] || d.descricao_situacao_cadastral || 'Ativa',
          endereco,
          cnae_principal: cnaePrincipal,
          cnaes_secundarios: cnaesSecundarios,
          email: form.EMAIL.trim(),
          telefone: form.TELEFONE.trim(),
        },
        { ...authHeader(), timeout: 60000 }
      );
      if (res.data.success) {
        setForm(prev => ({
          ...prev,
          'MISSÃO': res.data.missao || prev['MISSÃO'],
          SOBRE: res.data.sobre || prev.SOBRE,
          RODAPE: res.data.rodape || prev.RODAPE,
        }));
        setSuccess('Textos gerados pela IA! Revise abaixo e publique.');
      } else { setError(res.data.message || 'Falha ao gerar textos'); }
    } catch (err) { setError(err.response?.data?.message || 'Erro ao gerar textos com IA'); }
    finally { setIaLoading(false); }
  };

  const handleGenerate = async () => {
    setError(''); setSuccess(''); setLastUrl('');
    if (!form.DOMINIO.trim()) { setError('Informe o nome do site (slug).'); return; }
    setGenerating(true);
    try {
      // Converte quebras de linha em <br> nos textos longos (o template é HTML),
      // igual ao fluxo do FTM Legacy.
      const config = { ...form, DOMINIO: formatDomain(form.DOMINIO) };
      ['MISSÃO', 'SOBRE', 'RODAPE'].forEach(k => {
        if (config[k]) config[k] = String(config[k]).replace(/\n/g, '<br>');
      });
      const res = await axios.post(
        `${API}/api/cloudflare/generate`,
        { config },
        { ...authHeader(), timeout: 240000 }
      );
      if (res.data.success) {
        setLastUrl(res.data.url || '');
        setSuccess('Site publicado no Cloudflare com sucesso!');
        setForm({
          DOMINIO: '', 'RAZÃO': '', CNPJ: '', TELEFONE: '', EMAIL: '',
          WHATSAPP: '', INSTAGRAM: '', LINK_PAGINA: '', 'MISSÃO': '',
          SOBRE: '', RODAPE: '', META_TAG: '',
        });
        setCnpjInput('');
        setCnpjData(null);
        fetchSites();
      } else { setError(res.data.message || 'Falha ao publicar'); }
    } catch (err) { setError(err.response?.data?.message || 'Erro ao publicar no Cloudflare'); }
    finally { setGenerating(false); }
  };

  const handleDelete = async (site) => {
    if (!window.confirm(`Excluir o site "${site.name}" do Cloudflare? Esta ação remove o Worker publicado.`)) return;
    try {
      const res = await axios.delete(`${API}/api/cloudflare/sites/${site._id}`, authHeader());
      if (res.data.success) { setSuccess('Site excluído.'); fetchSites(); }
      else setError(res.data.message || 'Falha ao excluir');
    } catch (err) { setError(err.response?.data?.message || 'Erro ao excluir'); }
  };

  const handleCopy = (text, label) => {
    navigator.clipboard.writeText(text);
    setCopied(label);
    setTimeout(() => setCopied(''), 2000);
  };

  if (!isAdmin) return null;

  return (
    <Fade in timeout={400}>
      <Box sx={{ maxWidth: 880, mx: 'auto' }}>
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
            <Cloud size={22} color="#F38020" />
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#F9FAFB', letterSpacing: '-0.02em' }}>
              Sites Cloudflare
            </Typography>
            <Chip label="ADMIN" size="small" sx={{ background: 'rgba(243,128,32,0.15)', color: '#F38020', fontWeight: 700, fontSize: '0.7rem', height: 22 }} />
          </Box>
          <Typography variant="body2" sx={{ color: '#6B7280' }}>
            Publica o mesmo template do FTM Legacy no domínio gratuito <strong>.workers.dev</strong> do Cloudflare.
          </Typography>
        </Box>

        {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2, background: 'rgba(239,68,68,0.1)', color: '#FCA5A5', border: '1px solid rgba(239,68,68,0.2)', '& .MuiAlert-icon': { color: '#EF4444' } }}>{error}</Alert>}
        {success && <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 2, background: 'rgba(16,185,129,0.1)', color: '#6EE7B7', border: '1px solid rgba(16,185,129,0.2)', '& .MuiAlert-icon': { color: '#10B981' } }}>{success}</Alert>}

        {/* Status da integração */}
        {!cfConfig.loading && !cfConfig.configured && (
          <Alert severity="warning" icon={<AlertTriangle size={18} />} sx={{ mb: 3, background: 'rgba(245,158,11,0.08)', color: '#FCD34D', border: '1px solid rgba(245,158,11,0.15)', '& .MuiAlert-icon': { color: '#F59E0B' } }}>
            A integração com o Cloudflare ainda não foi configurada no servidor. Defina <strong>CLOUDFLARE_API_TOKEN</strong>, <strong>CLOUDFLARE_ACCOUNT_ID</strong> e <strong>CLOUDFLARE_WORKERS_SUBDOMAIN</strong> no <code>.env</code> do backend.
          </Alert>
        )}
        {!cfConfig.loading && cfConfig.configured && cfConfig.subdomain && (
          <Box sx={{ mb: 3, p: 1.5, borderRadius: 2, background: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.15)', display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckCircle2 size={16} color="#10B981" />
            <Typography sx={{ color: '#6EE7B7', fontSize: '0.82rem' }}>
              Conectado. Os sites ficarão em <strong>{`<nome>.${cfConfig.subdomain}.workers.dev`}</strong>
            </Typography>
          </Box>
        )}

        {/* Formulário */}
        <SectionCard elevation={0}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
            <Search size={20} color="#F38020" />
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#F9FAFB', fontSize: '1rem' }}>Dados do site</Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' }, mb: 3 }}>
            <TextField fullWidth label="CNPJ (opcional — preenche automático)" placeholder="00.000.000/0000-00" value={cnpjInput}
              onChange={(e) => setCnpjInput(formatCNPJ(e.target.value))}
              onKeyDown={(e) => e.key === 'Enter' && handleBuscarCNPJ()} />
            <Button variant="outlined" onClick={handleBuscarCNPJ} disabled={cnpjLoading}
              sx={{ minWidth: 140, height: 56, borderRadius: 2, textTransform: 'none', borderColor: 'rgba(243,128,32,0.4)', color: '#F38020', '&:hover': { borderColor: '#F38020', background: 'rgba(243,128,32,0.08)' } }}
              startIcon={cnpjLoading ? <CircularProgress size={18} sx={{ color: '#F38020' }} /> : <Search size={18} />}>
              {cnpjLoading ? 'Buscando...' : 'Buscar'}
            </Button>
          </Box>

          <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)', mb: 3 }} />

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Nome do site (slug)" required value={form.DOMINIO}
                onChange={(e) => setField('DOMINIO', formatDomain(e.target.value))}
                helperText={cfConfig.subdomain && form.DOMINIO ? `${form.DOMINIO}.${cfConfig.subdomain}.workers.dev` : 'Vira o subdomínio .workers.dev'}
                InputProps={{ endAdornment: <InputAdornment position="end"><Typography sx={{ color: '#6B7280', fontSize: '0.72rem' }}>{form.DOMINIO.length}/50</Typography></InputAdornment> }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Razão social" value={form['RAZÃO']} onChange={(e) => setField('RAZÃO', e.target.value)} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="CNPJ" value={form.CNPJ} onChange={(e) => setField('CNPJ', formatCNPJ(e.target.value))} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Telefone" value={form.TELEFONE} onChange={(e) => setField('TELEFONE', e.target.value)} placeholder="(31) 97139-1218" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="WhatsApp" value={form.WHATSAPP} onChange={(e) => setField('WHATSAPP', e.target.value)} placeholder="(31) 97139-1218" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Email" value={form.EMAIL} onChange={(e) => setField('EMAIL', e.target.value)} placeholder="contato@empresa.com.br" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Instagram (opcional)" value={form.INSTAGRAM} onChange={(e) => setField('INSTAGRAM', e.target.value)} placeholder="https://instagram.com/empresa" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Link da página/Facebook (opcional)" value={form.LINK_PAGINA} onChange={(e) => setField('LINK_PAGINA', e.target.value)} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Missão" value={form['MISSÃO']} onChange={(e) => setField('MISSÃO', e.target.value)} multiline minRows={2} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Sobre" value={form.SOBRE} onChange={(e) => setField('SOBRE', e.target.value)} multiline minRows={3} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Rodapé" value={form.RODAPE} onChange={(e) => setField('RODAPE', e.target.value)} multiline minRows={2} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Meta tag de verificação (opcional)" value={form.META_TAG} onChange={(e) => setField('META_TAG', e.target.value)}
                placeholder='<meta name="facebook-domain-verification" content="..." />' />
            </Grid>
          </Grid>

          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <Button variant="outlined"
              startIcon={iaLoading ? <CircularProgress size={16} sx={{ color: '#A78BFA' }} /> : <Sparkles size={18} />}
              onClick={handleGerarTextos} disabled={iaLoading || !cnpjData}
              sx={{ textTransform: 'none', borderColor: 'rgba(124,58,237,0.4)', color: '#A78BFA', '&:hover': { borderColor: '#7C3AED', background: 'rgba(124,58,237,0.08)' } }}>
              {iaLoading ? 'Gerando textos com IA...' : 'Gerar textos com IA (1 token)'}
            </Button>
          </Box>

          <Box sx={{ textAlign: 'center', mt: 2 }}>
            {generating ? (
              <Box sx={{ py: 2 }}>
                <CircularProgress size={36} sx={{ color: '#F38020', mb: 1.5 }} />
                <Typography sx={{ color: '#D1D5DB', fontWeight: 600 }}>Publicando no Cloudflare...</Typography>
                <Typography sx={{ color: '#6B7280', fontSize: '0.8rem', mt: 0.5 }}>Pode levar de 10 a 40 segundos</Typography>
              </Box>
            ) : (
              <Button variant="contained" size="large" startIcon={<Rocket size={20} />} onClick={handleGenerate}
                disabled={!cfConfig.configured || !form.DOMINIO.trim()}
                sx={{ px: 5, py: 1.5, borderRadius: 3, fontSize: '1rem', background: '#F38020', textTransform: 'none', fontWeight: 600, '&:hover': { background: '#d96f12' } }}>
                Publicar no Cloudflare
              </Button>
            )}
          </Box>

          {lastUrl && (
            <Box sx={{ mt: 3, p: 2, borderRadius: 2, background: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.2)', display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Globe size={18} color="#10B981" />
              <Typography sx={{ color: '#F9FAFB', fontSize: '0.9rem', flex: 1, wordBreak: 'break-all' }}>{lastUrl}</Typography>
              <Tooltip title={copied === 'new' ? 'Copiado!' : 'Copiar'}>
                <IconButton size="small" onClick={() => handleCopy(lastUrl, 'new')}>
                  {copied === 'new' ? <Check size={16} color="#10B981" /> : <Copy size={16} color="#6B7280" />}
                </IconButton>
              </Tooltip>
              <Tooltip title="Abrir">
                <IconButton size="small" onClick={() => window.open(lastUrl, '_blank')}>
                  <ExternalLink size={16} color="#6B7280" />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </SectionCard>

        {/* Lista de sites publicados */}
        <SectionCard elevation={0}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
            <Cloud size={20} color="#F38020" />
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#F9FAFB', fontSize: '1rem' }}>Sites publicados</Typography>
          </Box>

          {loadingSites ? (
            <Box sx={{ textAlign: 'center', py: 3 }}><CircularProgress size={26} sx={{ color: '#F38020' }} /></Box>
          ) : sites.length === 0 ? (
            <Typography sx={{ color: '#6B7280', fontSize: '0.88rem' }}>Nenhum site publicado ainda.</Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {sites.map((s) => (
                <Box key={s._id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 2, borderRadius: 2, background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
                  <Globe size={18} color="#F38020" />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ color: '#F9FAFB', fontWeight: 600, fontSize: '0.9rem' }}>{s.name}</Typography>
                    <Typography sx={{ color: '#9CA3AF', fontSize: '0.78rem', wordBreak: 'break-all' }}>{s.url || '(sem URL)'}</Typography>
                  </Box>
                  {s.url && (
                    <Tooltip title="Abrir">
                      <IconButton size="small" onClick={() => window.open(s.url, '_blank')}>
                        <ExternalLink size={16} color="#6B7280" />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title="Excluir">
                    <IconButton size="small" onClick={() => handleDelete(s)}>
                      <Trash2 size={16} color="#EF4444" />
                    </IconButton>
                  </Tooltip>
                </Box>
              ))}
            </Box>
          )}
        </SectionCard>
      </Box>
    </Fade>
  );
}
